import {useEffect, useRef, useState} from 'react';
import {isServer, loadScript, removeDOMNode} from 'coreUtils/utils';
import {isPayrailsRiskSDKActive} from 'coreUtils/payrailsRisk';
import {getRiskifiedSessionId} from 'coreUtils/riskified';

import useCustomerMgr from './useCustomerMgr';
import {useConfigurations, usePathname} from 'coreHooks';

const RISKIFIED_SCRIPT_ID = 'riskified-script';
const SID_POLL_INTERVAL_MS = 250;
const SID_MAX_ATTEMPTS = 40;

const useLoadRiskifiedScript = () => {
	const customerMgr = useCustomerMgr();
	const customer = customerMgr.getCurrentCustomer();
	const pathname = usePathname();
	const configurationsMgr = useConfigurations();
	const storeDomain = configurationsMgr.get('sitePreferences.riskifiedDomain');
	const [scriptReady, setScriptReady] = useState(false);
	const loadedSidRef = useRef<string | undefined>(undefined);

	useEffect(() => {
		if (!customerMgr.isContextAvailable()) {
			return;
		}

		if (isPayrailsRiskSDKActive(configurationsMgr.get('sitePreferences.payrailsRiskMigrationConfig'))) {
			return;
		}

		const load = (sid: string) => {
			removeDOMNode(RISKIFIED_SCRIPT_ID);

			const scriptUrl = `https://beacon.riskified.com?shop=${storeDomain}&sid=${sid}=`;
			loadScript(scriptUrl, RISKIFIED_SCRIPT_ID, {}, true)
				.then(() => {
					// The event is the canonical readiness signal. This fallback handles
					// an already-mounted Beacon whose ready event was emitted earlier.
					if (global.RISKX && typeof global.RISKX.go === 'function') {
						handleBeaconReady();
					}
				})
				.catch(() => setScriptReady(false));
		};

		const handleBeaconReady = () => setScriptReady(true);
		document.addEventListener('rskx_ready', handleBeaconReady);

		const cleanupBeaconReadyListener = () => document.removeEventListener('rskx_ready', handleBeaconReady);

		const sid = getRiskifiedSessionId();

		if (sid) {
			if (loadedSidRef.current === sid) {
				return cleanupBeaconReadyListener;
			}

			loadedSidRef.current = sid;
			setScriptReady(false);
			load(sid);

			return cleanupBeaconReadyListener;
		}

		let attempts = 0;
		const interval = setInterval(() => {
			const nextSid = getRiskifiedSessionId();
			attempts += 1;

			if (nextSid) {
				clearInterval(interval);

				if (loadedSidRef.current !== nextSid) {
					loadedSidRef.current = nextSid;
					setScriptReady(false);
					load(nextSid);
				}
			} else if (attempts >= SID_MAX_ATTEMPTS) {
				clearInterval(interval);
				console.error(`[Riskified] SID cookie not available after ${SID_MAX_ATTEMPTS} attempts (${SID_MAX_ATTEMPTS * SID_POLL_INTERVAL_MS}ms)`);
			}
		}, SID_POLL_INTERVAL_MS);

		return () => {
			cleanupBeaconReadyListener();
			clearInterval(interval);
		};
	}, [customerMgr.isContextAvailable(), customer?.isRegistered]);

	useEffect(() => {
		if (!isServer && scriptReady && global.RISKX && typeof global.RISKX.go === 'function') {
			global.RISKX.go(pathname);
		}
	}, [pathname, scriptReady]);
};

export default useLoadRiskifiedScript;
